import { ACTOR_TYPE, ROLE, TEAM_ROLE } from '@/shared/data/permissionSystem'
import OpeningTimesIcon from '~icons/lucide/calendar-clock'
import ChatAddIcon from '~icons/lucide/message-square-plus'
import VideoIcon from '~icons/lucide/video'
import LifeBuoyIcon from '~icons/lucide/life-buoy'
import UsersIcon from '~icons/lucide/users'
import { computed } from 'vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import BasketballIcon from '~icons/lucide/volleyball'
import AddIcon from '~icons/lucide/plus'
import RecyclingIcon from '~icons/lucide/repeat'
import { router, usePage } from '@inertiajs/vue3'
import SettingsGearIcon from '~icons/lucide/settings'
import BookmarkIcon from '~icons/lucide/bookmark'
import ChessKingOutline from '~icons/lucide/chess-queen'
import GoldCoinIcon from '~icons/lucide/circle-star'
import ShopIcon from '~icons/lucide/store'
import LeaveIcon from '~icons/lucide/log-out'
import axios from 'axios'
import RouteIcon from '~icons/lucide/route'
import FeedbackIcon from '~icons/lucide/message-square-text'

const _main = [
  {
    name: 'My Sessions',
    icon: OpeningTimesIcon,
    href: {
      [ACTOR_TYPE.MENTEE]: '/sessions',
      [ACTOR_TYPE.MENTOR]: '/mentor/sessions',
      [ACTOR_TYPE.NONE]: null,
    },
  },
  {
    name: 'Session Recordings',
    icon: VideoIcon,
    href: {
      [ACTOR_TYPE.MENTEE]: '/recordings',
      [ACTOR_TYPE.MENTOR]: '/recordings',
      [ACTOR_TYPE.NONE]: null,
    },
  },
  // Help Requests leaves once Ask is on: Community takes its slot.
  // Visitors do not see it.
  {
    name: 'Help Requests',
    icon: ChatAddIcon,
    hiddenWhenFlag: 'askEnabled',
    href: {
      [ACTOR_TYPE.MENTEE]: '/opportunities',
      [ACTOR_TYPE.MENTOR]: '/opportunities',
      [ACTOR_TYPE.NONE]: null,
    },
  },
  {
    name: 'Community',
    icon: UsersIcon,
    flag: 'communityShellEnabled',
    href: {
      [ACTOR_TYPE.MENTEE]: '/community',
      [ACTOR_TYPE.MENTOR]: '/community',
      [ACTOR_TYPE.NONE]: '/community',
    },
  },
]

export const useNavigationElements = () => {
  const { accessControl, isUserLoggedIn } = usePermissionSystem()
  const { currentUser } = useCurrentUser()
  const page = usePage()
  const referralEligible = computed(() => currentUser.value.referralEligible)

  // An item can require a feature flag (`flag`) or hide behind one
  // (`hiddenWhenFlag`). Both read the shared featureFlags by key.
  const isShownByFlags = (element) => {
    const featureFlags = page.props.shared.featureFlags
    if (element.flag && !featureFlags[element.flag]) return false
    if (element.hiddenWhenFlag && featureFlags[element.hiddenWhenFlag]) {
      return false
    }
    return true
  }

  const mainNavigation = computed(() => {
    return _main.filter((element) => {
      if (!isShownByFlags(element)) return false
      if (element.dropdown) {
        element.dropdown = element.dropdown.filter(
          (element) => element.href[accessControl.value.role],
        )
        return element.dropdown.length > 0
      } else {
        return element.href[accessControl.value.actorType]
      }
    })
  })
  const mainNavigationItemsInMoreButton = computed(() => {
    return _main.filter((item) => item.isInMoreButton && isShownByFlags(item))
  })

  const mentorDropdownNavigation = computed(() => {
    const teamNavbarElement =
      accessControl.value.teamRole === TEAM_ROLE.OWNER
        ? {
            as: 'InertiaLink',
            name: 'View Team',
            icon: BasketballIcon,
            href: '/team/members',
          }
        : accessControl.value.teamRole === TEAM_ROLE.NONE
          ? {
              as: 'InertiaLink',
              name: 'Create Team',
              icon: AddIcon,
              href: '/team/new',
            }
          : null

    return [
      {
        as: 'button',
        name:
          accessControl.value.actorType === ACTOR_TYPE.MENTOR
            ? 'Switch to mentee profile'
            : 'Switch to mentor profile',
        icon: RecyclingIcon,
        hasBottomSeparator: true,
        actions: {
          onClick: () => {
            router.patch('/switch-actor')
          },
        },
      },
      teamNavbarElement,
      {
        as: 'InertiaLink',
        name: 'Referral Program',
        icon: GoldCoinIcon,
        href: '/referrals',
      },
      // With the mentor chat on, the chat takes the place of the Roadmap.
      {
        as: 'InertiaLink',
        name: 'Growth Roadmap',
        icon: RouteIcon,
        href: '/roadmap',
        description: 'Three focus areas, a mentor for each',
        isNew: true,
        hiddenWhenFlag: 'mentorChatEnabled',
      },
      {
        as: 'InertiaLink',
        name: 'AI Matching',
        icon: RouteIcon,
        href: '/ai-matching',
        description: 'Tell us the problem, get your mentors',
        isNew: true,
        flag: 'mentorChatEnabled',
      },
      {
        as: 'InertiaLink',
        name: 'Lists',
        icon: BookmarkIcon,
        href: '/lists',
      },
      {
        as: 'InertiaLink',
        name: 'Leaderboard',
        icon: ChessKingOutline,
        href: '/leaderboard',
        hasBottomSeparator: true,
      },
      {
        as: 'InertiaLink',
        name: 'GrowthExpert',
        icon: ShopIcon,
        href: '/growthexpert',
        hasBottomSeparator: true,
      },
      {
        as: 'InertiaLink',
        name: 'Settings',
        icon: SettingsGearIcon,
        href: '/settings/account/sessions',
      },
      {
        as: 'a',
        name: 'Help & FAQs',
        icon: LifeBuoyIcon,
        href: '/faq/',
        attrs: { target: '_blank' },
      },
      {
        as: 'InertiaLink',
        name: 'Give feedback',
        icon: FeedbackIcon,
        href: '/feedback',
        hasBottomSeparator: true,
      },
      {
        as: 'button',
        name: 'Log out',
        icon: LeaveIcon,
        href: '/users/sign_out',
        attrs: {
          test_id: 'logout-button',
        },
        actions: {
          onClick: async () => {
            await axios.delete('/users/sign_out')
            window.location.href = '/users/sign_in'
            window.segmentAnalytics.reset()
          },
        },
      },
    ].filter(Boolean)
  })
  const menteeDropdownNavigation = computed(() => {
    const teamNavbarElement =
      accessControl.value.teamRole === TEAM_ROLE.OWNER
        ? {
            as: 'InertiaLink',
            name: 'View Team',
            icon: BasketballIcon,
            href: '/team/members',
          }
        : accessControl.value.teamRole === TEAM_ROLE.NONE
          ? {
              as: 'InertiaLink',
              name: 'Create Team',
              icon: AddIcon,
              href: '/team/new',
            }
          : null

    return [
      teamNavbarElement,
      referralEligible.value
        ? {
            as: 'InertiaLink',
            name: 'Referral Program',
            icon: GoldCoinIcon,
            href: '/referrals',
          }
        : null,
      // With the mentor chat on, the chat takes the place of the Roadmap.
      {
        as: 'InertiaLink',
        name: 'Growth Roadmap',
        icon: RouteIcon,
        href: '/roadmap',
        description: 'Three focus areas, a mentor for each',
        isNew: true,
        hiddenWhenFlag: 'mentorChatEnabled',
      },
      {
        as: 'InertiaLink',
        name: 'AI Matching',
        icon: RouteIcon,
        href: '/ai-matching',
        description: 'Tell us the problem, get your mentors',
        isNew: true,
        flag: 'mentorChatEnabled',
      },
      {
        as: 'InertiaLink',
        name: 'Lists',
        icon: BookmarkIcon,
        href: '/lists',
      },
      {
        as: 'InertiaLink',
        name: 'Leaderboard',
        icon: ChessKingOutline,
        href: '/leaderboard',
        hasBottomSeparator: true,
      },
      {
        as: 'InertiaLink',
        name: 'Settings',
        icon: SettingsGearIcon,
        href: '/settings/profile/basic',
      },
      {
        as: 'a',
        name: 'Help & FAQs',
        icon: LifeBuoyIcon,
        href: '/faq/',
        attrs: { target: '_blank' },
      },
      {
        as: 'InertiaLink',
        name: 'Give feedback',
        icon: FeedbackIcon,
        href: '/feedback',
        hasBottomSeparator: true,
      },
      {
        as: 'button',
        name: 'Log out',
        icon: LeaveIcon,
        actions: {
          onClick: async () => {
            await axios.delete('/users/sign_out')
            window.location.href = '/users/sign_in'
            window.segmentAnalytics.reset()
          },
        },
      },
    ].filter(Boolean)
  })
  const dropdownNavigation = computed(() => {
    if (!isUserLoggedIn.value) {
      return { [ROLE.MENTOR]: [], [ROLE.MENTEE]: [], [ROLE.GUEST]: [] }
    }
    return {
      [ROLE.MENTEE]: menteeDropdownNavigation.value,
      [ROLE.MENTOR]: mentorDropdownNavigation.value,
      [ROLE.GUEST]: [],
    }
  })

  return {
    mainNavigation,
    mainNavigationItemsInMoreButton,
    dropdownNavigation,
  }
}
